import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUpdateUserGeneralSetting } from "@/hooks/useUserQueries";

// Environment-aware logger (only logs in development)
const logger = import.meta.env.DEV ? console.log : () => {};
const loggerError = import.meta.env.DEV ? console.error : () => {};
const loggerWarn = import.meta.env.DEV ? console.warn : () => {};

// Timezone context type
interface TimezoneContextType {
  timezone: string;
  setTimezone: (tz: string) => void;
}

// Create context with default values
const TimezoneContext = createContext<TimezoneContextType>({
  timezone: "Asia/Shanghai", // I4: Default fallback (Invariant I4)
  setTimezone: () => {},
});

// Provider props
interface TimezoneProviderProps {
  children: React.ReactNode;
}

/**
 * TimezoneProvider manages user timezone state
 *
 * Features (v0.65.0):
 * - Auto-detects browser timezone on first load
 * - Persists timezone to user_setting via API
 * - Provides timezone to all child components
 *
 * Invariant I2: User timezone loaded from user_setting (backend), not API parameters
 * Invariant I4: Default fallback to Asia/Shanghai
 */
export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  const { currentUser, userGeneralSetting, refetchSettings } = useAuth();
  const { mutate: updateUserGeneralSetting } = useUpdateUserGeneralSetting(currentUser?.name);
  const [timezone, setTimezoneState] = useState<string>("Asia/Shanghai");
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize timezone from user setting or detect from browser
  useEffect(() => {
    const initializeTimezone = async () => {
      // First, try to get from user's GeneralSetting
      if (userGeneralSetting?.timezone) {
        logger("[TimezoneContext] Loaded timezone from user settings:", userGeneralSetting.timezone);
        setTimezoneState(userGeneralSetting.timezone);
        setIsInitialized(true);
        return;
      }

      // If not set, detect from browser and save to user_setting
      try {
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (browserTimezone && currentUser) {
          logger("[TimezoneContext] Detected browser timezone, saving to user settings:", browserTimezone);

          // Save to user_setting via API (useUpdateUserGeneralSetting will fetch existing and merge)
          updateUserGeneralSetting(
            {
              generalSetting: { timezone: browserTimezone }, // Partial<UserSetting_GeneralSetting>
              updateMask: ["timezone"],
            },
            {
              onSuccess: () => {
                logger("[TimezoneContext] Timezone saved successfully");
                refetchSettings(); // Refresh user settings
              },
              onError: (error) => {
                loggerError("[TimezoneContext] Failed to save timezone:", error);
                // Still use detected timezone even if save fails
                setTimezoneState(browserTimezone);
              },
            },
          );

          setTimezoneState(browserTimezone);
        }
      } catch (error) {
        loggerError("[TimezoneContext] Failed to detect timezone:", error);
        // Fallback to default timezone on error (Invariant I4)
      } finally {
        setIsInitialized(true);
      }
    };

    if (!isInitialized && currentUser) {
      initializeTimezone();
    }
  }, [isInitialized, currentUser, userGeneralSetting, updateUserGeneralSetting, refetchSettings]);

  // Update timezone function
  const setTimezone = (tz: string) => {
    logger("[TimezoneContext] Setting timezone:", tz);

    if (!currentUser) {
      loggerWarn("[TimezoneContext] No current user, cannot save timezone");
      setTimezoneState(tz);
      return;
    }

    // Update local state immediately
    setTimezoneState(tz);

    // Save to user_setting (useUpdateUserGeneralSetting will fetch existing and merge)
    updateUserGeneralSetting(
      {
        generalSetting: { timezone: tz }, // Partial<UserSetting_GeneralSetting>
        updateMask: ["timezone"],
      },
      {
        onSuccess: () => {
          logger("[TimezoneContext] Timezone updated successfully");
          refetchSettings();
        },
        onError: (error) => {
          loggerError("[TimezoneContext] Failed to update timezone:", error);
        },
      },
    );
  };

  return <TimezoneContext.Provider value={{ timezone, setTimezone }}>{children}</TimezoneContext.Provider>;
};

/**
 * useTimezone hook to access timezone context
 *
 * Usage:
 * ```tsx
 * const { timezone, setTimezone } = useTimezone();
 * console.log(timezone); // "Asia/Shanghai" or user's timezone
 *
 * // Update timezone:
 * setTimezone("America/New_York");
 * ```
 */
export const useTimezone = (): TimezoneContextType => {
  const context = useContext(TimezoneContext);
  return context;
};

export default TimezoneContext;
